import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowRight, Phone, MapPin, ShieldCheck, CheckCircle2, Users, Sparkles,
  Bed, Bath, Home as HomeIcon, Car, Clock, Calendar,
  Accessibility, ShoppingBag, Train, HeartPulse, Trees,
  Star, ArrowDownToLine, DoorOpen, ShowerHead, Layers,
} from "lucide-react";
import {
  PROPERTIES, PROPERTY_BY_SLUG, statusBadge, vacancyLabel, propertyView,
} from "../data/properties";
import { useProperties } from "../lib/useProperties";
import {
  ACCESSIBILITY_TAGS, SUITABILITY_TAGS, SDA_CATEGORIES,
  SUPPORT_RATIOS, SUPPORT_MODELS,
} from "../data/propertyModel";
import PropertyGallery from "../components/PropertyGallery";
import SaveHomeButton from "../components/SaveHomeButton";
import PropertyListingCard from "../components/PropertyListingCard";
import NotFoundPage from "./NotFoundPage";
import { useSEO, breadcrumbSchema, SITE_URL } from "../lib/seo";
import { trackPhoneClick } from "../lib/tracking";

const A11Y_LABEL = Object.fromEntries(ACCESSIBILITY_TAGS.map((t) => [t.value, t.label]));
const SUIT_LABEL = Object.fromEntries(SUITABILITY_TAGS.map((t) => [t.value, t.label]));
const RATIO_LABEL = Object.fromEntries(SUPPORT_RATIOS.map((t) => [t.value, t.label]));
const MODEL_LABEL = Object.fromEntries(SUPPORT_MODELS.map((t) => [t.value, t.label]));
const SDA_LABEL = Object.fromEntries(SDA_CATEGORIES.map((t) => [t.value, t.label]));

const A11Y_ICON_MAP = [
  { match: /step-?free|level/, Icon: ArrowDownToLine },
  { match: /doorway|door/, Icon: DoorOpen },
  { match: /shower|bathroom/, Icon: ShowerHead },
  { match: /hoist/, Icon: Accessibility },
  { match: /kitchen/, Icon: HomeIcon },
  { match: /parking|driveway/, Icon: Car },
  { match: /outdoor|yard|garden/, Icon: Trees },
  { match: /single-?storey|floor|lift/, Icon: Layers },
];
const iconForA11y = (value) => (A11Y_ICON_MAP.find((m) => m.match.test(value))?.Icon) || Accessibility;

function relativeTimeSince(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.max(0, Math.round((Date.now() - then) / (1000 * 60 * 60 * 24)));
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.round(days / 7)} weeks ago`;
  return `${Math.round(days / 30)} months ago`;
}

const NEARBY_ICON = { transport: Train, shops: ShoppingBag, medical: HeartPulse, community: Trees };
const NEARBY_LABEL = { transport: "Transport", shops: "Shops & essentials", medical: "Medical & allied health", community: "Community & parks" };

/**
 * Hero — property image with dark overlay, badges, name, one-line positioning,
 * and a quick-facts strip (vacancy-first, no $/week).
 */
function Hero({ p, sb, vacancy, reviewedAgo }) {
  const heroImg = p.hero_image || (p.images && p.images[0] && p.images[0].src);
  const heroAlt = (p.images && p.images[0] && p.images[0].alt) || p.name;
  const serviceLabel = p.sda_category ? "SDA" : "SIL";
  return (
    <section className="relative min-h-[540px] md:min-h-[600px] overflow-hidden text-white">
      <img src={heroImg} alt={heroAlt} loading="eager" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F0625]/45 via-[#0F0625]/60 to-[#0F0625]/95" />

      <div className="relative alpha-container pt-20 pb-10 md:pt-24 md:pb-14 flex flex-col justify-end min-h-[540px] md:min-h-[600px]">
        <div className="absolute top-6 right-4 md:right-8 z-10">
          <SaveHomeButton slug={p.slug} homeName={p.name} variant="overlay" size="lg" testid={`prop-${p.slug}-save`} />
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="alpha-chip !py-1.5 !px-3 !text-[10.5px] font-bold uppercase tracking-[0.22em] !bg-white !text-[color:var(--alpha-purple-800)] !border-white">
            {serviceLabel}
          </span>
          <span className={`alpha-chip !py-1.5 !px-3 !text-[10.5px] font-semibold uppercase tracking-[0.18em] ${
            sb.tone === "green" ? "!bg-[color:var(--alpha-green-600)] !text-white !border-[color:var(--alpha-green-700)]"
            : sb.tone === "purple" ? "!bg-[color:var(--alpha-purple-700)] !text-white !border-[color:var(--alpha-purple-800)]"
            : sb.tone === "amber" ? "!bg-[#FDF3E1] !text-[#8C5A0D] !border-[#F1D9A6]"
            : "!bg-white !text-[color:var(--alpha-ink)] !border-white"
          }`}>
            {sb.label}
          </span>
          {p.recordVerificationStatus === "demonstration" && (
            <span className="alpha-chip !py-1.5 !px-3 !text-[10.5px] font-semibold uppercase tracking-[0.18em] !bg-black/70 !text-white !border-transparent" data-testid={`prop-${p.slug}-demo`}>Demo listing</span>
          )}
        </div>

        <div className="text-[11px] tracking-[0.32em] uppercase text-white/75 font-semibold mb-3">
          <MapPin className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5" />{p.suburb}, {p.state}
        </div>
        <h1 className="font-display font-medium tracking-tight leading-[1.02] max-w-3xl" style={{ fontSize: "clamp(32px, 5.5vw, 60px)" }}>
          {p.name}
        </h1>
        {p.oneLiner && (
          <p className="mt-4 text-base md:text-lg text-white/90 max-w-2xl">{p.oneLiner}</p>
        )}

        <div className="mt-6 flex flex-wrap gap-2.5">
          <Link to={`/homes/${p.slug}/enquire`} className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-3.5 font-semibold inline-flex items-center gap-2 hover:scale-[1.02] transition-transform" data-testid={`prop-${p.slug}-cta-enquire`}>
            {p.listing_status === "upcoming" || p.listing_status === "eoi" ? "Register interest" : "Enquire about this home"} <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", `prop-${p.slug}-hero`)} className="bg-white/10 backdrop-blur border border-white/30 text-white rounded-full px-6 py-3.5 font-semibold inline-flex items-center gap-2 hover:bg-white/20 transition-colors">
            <Phone className="w-4 h-4" /> 1300 380 692
          </a>
        </div>
      </div>

      {/* Quick-facts strip */}
      <div className="relative -mt-6 md:-mt-8">
        <div className="alpha-container">
          <div className="rounded-2xl bg-white shadow-xl border border-[color:var(--alpha-line)] p-4 md:p-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4" data-testid={`prop-${p.slug}-quickfacts`}>
            <QF Icon={Sparkles} label="Vacancy" value={vacancy} tone="green" />
            <QF Icon={Bed} label="Participant beds" value={`${p.participant_bedrooms || p.total_bedrooms || 0}`} />
            <QF Icon={Bath} label="Bathrooms" value={`${p.bathrooms || 0}`} />
            <QF Icon={Users} label="Support ratio" value={(p.support_ratios || []).map((r) => RATIO_LABEL[r] || r).join(" / ") || "—"} />
            <QF Icon={Clock} label="Support model" value={(p.support_model || []).slice(0, 2).map((m) => MODEL_LABEL[m] || m).join(" · ") || "—"} />
            <QF Icon={Accessibility} label="Design category" value={p.sda_category ? SDA_LABEL[p.sda_category] : "SIL — non-SDA"} />
            <QF Icon={Calendar} label={p.available_from ? "Available from" : "Reviewed"} value={p.available_from || (reviewedAgo ? reviewedAgo : "—")} />
          </div>
        </div>
      </div>
    </section>
  );
}

function QF({ Icon, label, value, tone }) {
  return (
    <div className="flex items-start gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
        tone === "green" ? "bg-[color:var(--alpha-green-50)] text-[color:var(--alpha-green-700)]" : "bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)]"
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10.5px] uppercase tracking-[0.15em] font-semibold text-[color:var(--alpha-mute)]">{label}</div>
        <div className={`text-sm font-semibold ${tone === "green" ? "text-[color:var(--alpha-green-700)]" : "text-[color:var(--alpha-ink)]"} truncate`}>{value}</div>
      </div>
    </div>
  );
}

function StickyEnquiry({ p, vacancy, reviewedAgo }) {
  return (
    <aside className="lg:col-span-4">
      <div className="lg:sticky lg:top-32">
        <div className="alpha-card p-6" data-testid={`prop-${p.slug}-sticky-enquiry`}>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--alpha-green-700)] font-semibold">{vacancy}</div>
          <h2 className="mt-1 font-display text-xl font-semibold">Enquire about this home</h2>
          <p className="mt-2 text-sm text-[color:var(--alpha-mute)]">A short enquiry — we'll come back within 1 business day with an honest fit assessment. SIL support is separate from housing, and we'll walk you through both.</p>

          <div className="mt-5 space-y-2">
            <Link to={`/homes/${p.slug}/enquire`} className="alpha-btn-primary w-full justify-center" data-testid={`prop-${p.slug}-sticky-cta`}>
              Continue to enquiry form <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", `prop-${p.slug}-sticky`)} className="alpha-btn-green w-full justify-center">
              <Phone className="w-4 h-4" /> 1300 380 692
            </a>
          </div>

          <div className="mt-5 pt-5 border-t border-[color:var(--alpha-line)] space-y-2 text-sm">
            <div className="flex items-center gap-2 text-[color:var(--alpha-mute)]"><ShieldCheck className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> Registered NDIS provider</div>
            <div className="flex items-center gap-2 text-[color:var(--alpha-mute)]"><Users className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> Named intake person on every enquiry</div>
            {reviewedAgo && (
              <div className="flex items-center gap-2 text-[color:var(--alpha-mute)]"><Clock className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> Vacancy reviewed {reviewedAgo}</div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}

function WhyThisHome({ p }) {
  // Data-driven bullets — never invents features that aren't present.
  const bullets = [];
  if (p.vacancies > 0) {
    bullets.push(`${vacancyLabel(p)} — participant bedrooms are ready to view now.`);
  } else if (p.listing_status === "upcoming") {
    bullets.push(`Opening ${p.available_from || "soon"} — expressions of interest are open.`);
  }
  if (p.support_ratios?.length) {
    bullets.push(`Support delivered at ${p.support_ratios.map((r) => RATIO_LABEL[r] || r).join(" or ")} — with a ${p.support_model?.includes("individualised") ? "personalised" : "shared"} model where practical.`);
  }
  const flagshipA11y = (p.accessibility_features || []).slice(0, 3).map((v) => A11Y_LABEL[v] || v);
  if (flagshipA11y.length) bullets.push(`Accessibility strengths: ${flagshipA11y.join(", ")}.`);
  if (p.nearby?.medical) bullets.push(`Close to key services — ${p.nearby.medical}.`);
  const forensic = (p.suitability_tags || []).find((t) => ["forensic", "complex-health", "hospital-discharge", "behaviours-of-concern", "high-intensity"].includes(t));
  if (forensic) bullets.push(`Suitable for referrals involving ${SUIT_LABEL[forensic]?.toLowerCase() || forensic}.`);

  if (bullets.length === 0) return null;

  return (
    <section className="alpha-section-tight" data-testid={`prop-${p.slug}-why`}>
      <div className="alpha-overline">Why this home may be a good fit</div>
      <h2 className="mt-3 font-display text-2xl sm:text-3xl font-medium">A quick summary before you dig in.</h2>
      <ul className="mt-5 space-y-2.5">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3">
            <CheckCircle2 className="w-4 h-4 text-[color:var(--alpha-green-700)] mt-1 shrink-0" />
            <span className="text-[15px] text-[color:var(--alpha-ink)] leading-relaxed">{b}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AboutHome({ p }) {
  // Prefer richer legacy content (description[] / story.paragraphs) when present;
  // otherwise fall back to oneLiner + basic composed summary.
  const paragraphs =
    (p.description && p.description.length ? p.description : null)
    || (p.story?.paragraphs && p.story.paragraphs.length ? p.story.paragraphs : null)
    || [
      p.oneLiner,
      `${p.name} is a ${p.property_type} of ${p.total_bedrooms || p.participant_bedrooms || "several"} bedrooms in ${p.suburb}, ${p.state}. ${p.outdoor_area ? "It has a practical outdoor area, " : ""}${p.secure_yard ? "a secure yard, " : ""}and ${p.bathrooms || "well-appointed"} bathrooms designed to suit the participants living here.`,
    ].filter(Boolean);

  return (
    <section className="alpha-section-tight" data-testid={`prop-${p.slug}-about`}>
      <div className="alpha-overline">About the home</div>
      <h2 className="mt-3 font-display text-2xl sm:text-3xl font-medium">The home, in plain English.</h2>
      <div className="mt-5 space-y-4 max-w-3xl text-[15px] text-[color:var(--alpha-ink)] leading-relaxed">
        {paragraphs.map((para, i) => <p key={i}>{para}</p>)}
      </div>
      {p.features?.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          {p.features.map((f, i) => (
            <span key={`${f}-${i}`} className="alpha-chip alpha-chip-neutral">{f}</span>
          ))}
        </div>
      )}
    </section>
  );
}

function SupportModel({ p }) {
  const items = [];
  if (p.support_ratios?.length) items.push({ label: "Support ratio", value: p.support_ratios.map((r) => RATIO_LABEL[r] || r).join(" or ") });
  if (p.support_model?.length) items.push({ label: "Support model", value: p.support_model.map((m) => MODEL_LABEL[m] || m).join(" · ") });
  const sharedOrIndiv = p.support_model?.includes("individualised") ? "Personalised (1:1) supports where funded"
    : p.support_model?.includes("shared") ? "Shared supports across participants in the home"
    : null;
  if (sharedOrIndiv) items.push({ label: "Living arrangement", value: sharedOrIndiv });
  if (p.support_model?.includes("24-7")) items.push({ label: "Staff presence", value: "24/7 staffing on site" });
  else if (p.support_model?.includes("active-overnight")) items.push({ label: "Staff presence", value: "Active overnight supports" });
  else if (p.support_model?.includes("sleepover")) items.push({ label: "Staff presence", value: "Sleepover overnight" });
  if (p.ooa_room) items.push({ label: "OOA room", value: "Dedicated staff on-call room" });

  if (items.length === 0) return null;

  return (
    <section className="alpha-section-tight bg-[color:var(--alpha-bg-soft)] rounded-2xl p-6 md:p-8" data-testid={`prop-${p.slug}-support-model`}>
      <div className="alpha-overline">SIL support model</div>
      <h2 className="mt-3 font-display text-2xl sm:text-3xl font-medium">How support works in this home.</h2>
      <p className="mt-3 text-[color:var(--alpha-mute)] max-w-2xl">Support scope, ratios and overnight arrangements are a factual description of how this home operates today. Individual participant supports are agreed through your NDIS plan.</p>
      <dl className="mt-6 grid sm:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.label} className="alpha-card p-4">
            <dt className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[color:var(--alpha-mute)]">{it.label}</dt>
            <dd className="mt-1 font-display text-base font-semibold">{it.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function AccessibilitySection({ p }) {
  const features = p.accessibility_features || [];
  if (features.length === 0) return null;
  return (
    <section className="alpha-section-tight" data-testid={`prop-${p.slug}-accessibility`}>
      <div className="alpha-overline">Accessibility</div>
      <h2 className="mt-3 font-display text-2xl sm:text-3xl font-medium">Verified accessibility in this home.</h2>
      <p className="mt-3 text-[color:var(--alpha-mute)] max-w-2xl">Only features actually verified for this property are listed. If you need something specific that isn't here, please ask.</p>
      <ul className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {features.map((f) => {
          const Icon = iconForA11y(f);
          return (
            <li key={f} className="alpha-card p-4 flex items-start gap-3" data-testid={`prop-${p.slug}-a11y-${f}`}>
              <div className="w-10 h-10 rounded-xl bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center shrink-0">
                <Icon className="w-4.5 h-4.5" />
              </div>
              <span className="text-sm font-semibold text-[color:var(--alpha-ink)] mt-2">{A11Y_LABEL[f] || f}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function WhoMaySuit({ p }) {
  const tags = p.suitability_tags || [];
  if (tags.length === 0) return null;
  return (
    <section className="alpha-section-tight bg-[color:var(--alpha-bg-soft)] rounded-2xl p-6 md:p-8" data-testid={`prop-${p.slug}-suit`}>
      <div className="alpha-overline">Who this home may suit</div>
      <h2 className="mt-3 font-display text-2xl sm:text-3xl font-medium">Referral discovery — not clinical eligibility.</h2>
      <p className="mt-3 text-[color:var(--alpha-mute)] max-w-2xl">This home may suit participants with the following referral considerations. Final suitability is always confirmed by our intake team.</p>
      <ul className="mt-6 grid sm:grid-cols-2 gap-3">
        {tags.map((t) => (
          <li key={t} className="alpha-card p-4 flex items-start gap-3">
            <Star className="w-4 h-4 text-[color:var(--alpha-purple-700)] mt-1 shrink-0" />
            <span className="text-sm font-medium text-[color:var(--alpha-ink)] mt-0.5">{SUIT_LABEL[t] || t}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LocationSection({ p }) {
  const nearby = p.nearby || {};
  const rows = Object.keys(NEARBY_ICON).map((k) => ({ k, value: nearby[k] })).filter((r) => r.value);
  if (rows.length === 0) return null;

  return (
    <section className="alpha-section-tight" data-testid={`prop-${p.slug}-location`}>
      <div className="alpha-overline">Location & community</div>
      <h2 className="mt-3 font-display text-2xl sm:text-3xl font-medium">Well-connected in {p.suburb}.</h2>
      <p className="mt-3 text-[color:var(--alpha-mute)] max-w-2xl">General neighbourhood information — precise address is shared once your enquiry is confirmed.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {rows.map((r) => {
          const Icon = NEARBY_ICON[r.k];
          return (
            <div key={r.k} className="alpha-card p-5 flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[color:var(--alpha-mute)]">{NEARBY_LABEL[r.k]}</div>
                <div className="mt-1 text-sm text-[color:var(--alpha-ink)]">{r.value}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function GallerySection({ p }) {
  const images = p.images || (p.hero_image ? [{ src: p.hero_image, alt: p.name }] : []);
  if (images.length === 0) return null;
  return (
    <section className="alpha-section-tight" data-testid={`prop-${p.slug}-gallery`}>
      <div className="alpha-overline">Gallery</div>
      <h2 className="mt-3 font-display text-2xl sm:text-3xl font-medium">A closer look inside.</h2>
      <div className="mt-6">
        <PropertyGallery images={images} name={p.name} />
      </div>
    </section>
  );
}

function TrustBand({ p, reviewedAgo }) {
  const items = [
    { Icon: ShieldCheck, title: "Registered NDIS Provider", body: "Audited against the NDIS Practice Standards." },
    { Icon: Users, title: "Support model designed around the participant", body: "Ratios, overnight and daily supports agreed with the participant, family and coordinator." },
    { Icon: CheckCircle2, title: "Enquiry reviewed by Alpha intake", body: "Named intake person, honest capacity feedback within 2 business hours." },
  ];
  if (reviewedAgo) items.push({ Icon: Clock, title: `Vacancy reviewed ${reviewedAgo}`, body: "We keep our vacancy status current so referrals aren't wasted." });

  return (
    <section className="alpha-section-tight" data-testid={`prop-${p.slug}-trust`}>
      <div className="alpha-overline">Why refer to Alpha</div>
      <h2 className="mt-3 font-display text-2xl sm:text-3xl font-medium">Reassurance for families and coordinators.</h2>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((it) => (
          <div key={it.title} className="alpha-card p-5">
            <div className="w-10 h-10 rounded-xl bg-[color:var(--alpha-green-50)] text-[color:var(--alpha-green-700)] flex items-center justify-center">
              <it.Icon className="w-4.5 h-4.5" />
            </div>
            <div className="mt-3 font-display text-base font-semibold">{it.title}</div>
            <div className="mt-1 text-sm text-[color:var(--alpha-mute)]">{it.body}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RelatedHomes({ p }) {
  // Prefer same state, then any home; exclude self; up to 3.
  const sameState = PROPERTIES.filter((o) => o.slug !== p.slug && o.state === p.state);
  const others = PROPERTIES.filter((o) => o.slug !== p.slug && o.state !== p.state);
  const related = [...sameState, ...others].slice(0, 3);
  if (related.length === 0) return null;

  return (
    <section className="alpha-section-tight" data-testid={`prop-${p.slug}-related`}>
      <div className="alpha-overline">Related homes</div>
      <h2 className="mt-3 font-display text-2xl sm:text-3xl font-medium">
        {sameState.length > 0 ? `Other homes in ${p.state}.` : "Other homes worth exploring."}
      </h2>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {related.map((r) => <PropertyListingCard key={r.slug} property={r} />)}
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/vacancies" className="alpha-btn-ghost text-sm">See all homes <ArrowRight className="w-4 h-4" /></Link>
        <Link to="/referrals/participant" className="alpha-btn-green text-sm">Make a general SIL referral <ArrowRight className="w-4 h-4" /></Link>
        <Link to="/for-support-coordinators" className="alpha-btn-ghost text-sm">Speak with Support Coordination <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </section>
  );
}

export default function PropertyDetailPage(props) {
  const params = useParams();
  const slug = props.slug || params.slug;
  const { properties: LIVE_PROPERTIES } = useProperties();
  const raw = LIVE_PROPERTIES.find((x) => x.slug === slug) || PROPERTY_BY_SLUG[slug];
  const p = raw ? propertyView(raw) : null;

  useSEO({
    title: p ? `${p.name} — ${p.suburb}, ${p.state} · ${p.sda_category ? "SDA" : "SIL"} Home | Alpha Community Care`
      : "SIL Home | Alpha Community Care",
    description: p
      ? (p.oneLiner || `${p.name} in ${p.suburb}, ${p.state}. ${vacancyLabel(p)}. ${p.support_ratios?.[0] ? `Support at ${p.support_ratios.join(" or ")}. ` : ""}Registered NDIS provider.`).slice(0, 240)
      : "Explore NDIS SIL and SDA vacancies with Alpha Community Care.",
    keywords: p ? [
      `${p.sda_category ? "SDA" : "SIL"} ${p.suburb}`, `NDIS home ${p.suburb}`,
      `${p.sda_category ? "SDA" : "SIL"} ${p.state}`, ...(p.support_ratios || []).map((r) => `${r} support`),
      ...(p.accessibility_features || []).slice(0, 3).map((v) => A11Y_LABEL[v] || v),
    ] : [],
    canonical: p ? `${SITE_URL}${p.href || `/homes/${p.slug}`}` : `${SITE_URL}/vacancies`,
    jsonLd: p ? [
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Our homes", path: "/vacancies" },
        { name: p.name, path: p.href || `/homes/${p.slug}` },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "Accommodation",
        "name": p.name,
        "description": p.oneLiner || "",
        "url": `${SITE_URL}${p.href || `/homes/${p.slug}`}`,
        "numberOfBedrooms": p.participant_bedrooms || p.total_bedrooms || undefined,
        "numberOfBathroomsTotal": p.bathrooms || undefined,
        "image": [p.hero_image, ...(p.images || []).map((i) => i.src)].filter(Boolean),
        "address": {
          "@type": "PostalAddress",
          "addressLocality": p.suburb,
          "addressRegion": p.state,
          "postalCode": p.postcode || undefined,
          "addressCountry": "AU",
        },
        "amenityFeature": (p.accessibility_features || []).map((f) => ({
          "@type": "LocationFeatureSpecification",
          "name": A11Y_LABEL[f] || f,
          "value": true,
        })),
      },
    ] : [],
  });

  if (!raw) return <NotFoundPage />;

  const sb = statusBadge(p);
  const vacancy = vacancyLabel(p);
  const reviewedAgo = relativeTimeSince(p.vacancy_last_updated);

  return (
    <div data-testid={`page-property-${p.slug}`}>
      <Hero p={p} sb={sb} vacancy={vacancy} reviewedAgo={reviewedAgo} />

      <section className="alpha-container py-10 lg:py-14">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-8 space-y-2">
            <WhyThisHome p={p} />
            <AboutHome p={p} />
            <SupportModel p={p} />
            <AccessibilitySection p={p} />
            <WhoMaySuit p={p} />
            <LocationSection p={p} />
            <GallerySection p={p} />
            <TrustBand p={p} reviewedAgo={reviewedAgo} />
          </div>
          <StickyEnquiry p={p} vacancy={vacancy} reviewedAgo={reviewedAgo} />
        </div>
      </section>

      <div className="alpha-container pb-16">
        <RelatedHomes p={p} />
      </div>
    </div>
  );
}
